import uuid from 'uuid'
import { IHeaders, ICustomHTTPError } from '../types/http'
import { authToken, redirectToSphinx } from './auth'
import { API } from '../config'

const CustomHTTPError = (
  code: number,
  message: string,
  correlationId?: string
): ICustomHTTPError => ({
  code,
  message,
  correlationId,
})

export const processUnhandledResponse = async (res: Response) => {
  const doc = await res.json()
  if (res.ok) {
    return doc
  } else {
    const correlationId =
      res.headers.get('x-tr-sphinx-correlationid') ?? undefined
    throw CustomHTTPError(
      res.status,
      doc.error_description || doc.Message || doc.message || doc.errorMessage,
      correlationId
    )
  }
}

export const checkStatus = async (res: Response) => {
  switch (res.status) {
    case 202:
      return { location: res.headers.get('location') }
    case 201:
      const json = await res.json()
      return { ...json, location: res.headers.get('location') }
    case 204:
      return {}
    case 403:
    case 401:
      redirectToSphinx()
      return
    case 304:
      return null
    default:
      return processUnhandledResponse(res)
  }
}

export const safeFetch = (
  url: string,
  options = {},
  headers: IHeaders[] = []
) => {
  const request = new Request(url, options)
  request.headers.append('Authorization', `Bearer ${authToken.get()}`)
  const correlationId = uuid()
  request.headers.append('x-tr-sphinx-correlationid', correlationId)
  headers.map(({ name, value }) => request.headers.append(name, value))

  return fetch(request).then(checkStatus)
}

export const safeGet = (url: string) => safeFetch(url)

export const safeGetWithModified = (url: string, modified: string) =>
  safeFetch(url, {}, [
    {
      name: 'If-Modified-Since',
      value: modified,
    },
  ])

export const safePost = <T>(url: string, payload: T) =>
  safeFetch(url, {
    method: 'POST',
    mode: 'cors',
    body: payload && JSON.stringify(payload),
  })

export const safePut = <T>(url: string, payload: T) =>
  safeFetch(url, {
    method: 'PUT',
    mode: 'cors',
    body: payload && JSON.stringify(payload),
  })

export const safeDelete = (url: string) =>
  safeFetch(url, {
    method: 'DELETE',
    mode: 'cors',
  })

export const sphinxCheckStatus = (res: Response) =>
  res.status === 204 ? {} : processUnhandledResponse(res)

export const sphinxSafeFetch = async (
  url: string,
  options = {}
): Promise<any> => {
  const request = new Request(url, { ...options, mode: 'cors' })
  request.headers.append('Authorization', `Bearer ${authToken.get()}`)
  request.headers.append(
    'X-Api-Key',
    process.env.REACT_APP_SPHINX_API_KEY || ''
  )
  request.headers.set('Content-Type', 'application/json')
  return fetch(request).then(sphinxCheckStatus)
}

export const sphinxSafePost = <T>(url: string, payload: T) =>
  sphinxSafeFetch(url, {
    method: 'POST',
    body: payload && JSON.stringify(payload),
  })

export const sphinxSafePut = <T>(url: string, payload: T) =>
  sphinxSafeFetch(url, {
    method: 'PUT',
    body: payload && JSON.stringify(payload),
  })

export const sphinxSafeDelete = (url: string) =>
  sphinxSafeFetch(url, {
    method: 'DELETE',
  })

export const dataStreamSafeFetch = async (
    url: string,
    options = {}
  ): Promise<any> => {
    const request = new Request(url, { ...options, mode: 'cors' })
    request.headers.append('Authorization', `Bearer ${authToken.get()}`)
    request.headers.append(
      'X-Api-Key',
      process.env.REACT_APP_LAP_API_KEY || ''
    )
    request.headers.set('Content-Type', 'application/json')
    return fetch(request).then(sphinxCheckStatus)
  }
export const dataStreamSafePost = <T>(url: string, payload: T) =>
  dataStreamSafeFetch(url, {
    method: 'POST',
    mode: 'cors',
    body: payload && JSON.stringify(payload),
  })

export const baseUrl = `${API.API_HOST}/${API.API_VERSION}`
export const schedulesHostUrl = `${API.SCHEDULES_API_HOST}/${API.API_VERSION}`
export const datastreamUrl = `${API.DATASTREAM_API_HOST}/proxy/datastream/${API.DATASTREAM_API_VERSION}`
export const groupsHostUrl = `${API.GROUPS_API_HOST}/${API.API_VERSION}`
export const publishingHostUrl = `${API.PUBLISHING_API_HOST}/${API.API_VERSION}`
export const typeaheadHostUrl = `${API.TYPEAHEAD_API_HOST}/${API.API_VERSION}`
export const tagsHostUrl = `${API.TAGS_API_HOST}/${API.API_VERSION}`
export const sphinxPreferencesHostUrl = `${API.SPHINX_API_HOST}/preference/${API.API_VERSION}`

export const transformToHttps = (url: string) => url.replace(/^http:/, 'https:')
